import type { ProgrammeEntry } from "./types";

export type ProgrammeFilters = {
  query: string;
  wing: "all" | ProgrammeEntry["wing"];
  country: string;
  region: string;
  sportFamily: string;
  environment: string;
};

export type ProgrammeFacets = {
  countries: string[];
  regions: string[];
  sportFamilies: string[];
  environments: string[];
};

export const PROGRAMME_BATCH_SIZE = 12;

export const EMPTY_PROGRAMME_FILTERS: ProgrammeFilters = {
  query: "",
  wing: "all",
  country: "",
  region: "",
  sportFamily: "",
  environment: "",
};

function normalise(value: string): string {
  return value.normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLocaleLowerCase("en-GB")
    .trim();
}

function getSearchableValues(entry: ProgrammeEntry): string[] {
  const place = entry.programme.place;
  const sport = entry.programme.sport;

  return [
    entry.title,
    entry.subtitle,
    entry.wing,
    ...entry.programme.searchTerms,
    ...(place ? [place.country, place.region] : []),
    ...(sport ? [sport.family, ...sport.environments] : []),
  ];
}

export function filterProgramme(
  entries: ProgrammeEntry[],
  filters: ProgrammeFilters,
): ProgrammeEntry[] {
  const query = normalise(filters.query);
  const country = normalise(filters.country);
  const region = normalise(filters.region);
  const sportFamily = normalise(filters.sportFamily);
  const environment = normalise(filters.environment);

  return entries.filter((entry) => {
    const place = entry.programme.place;
    const sport = entry.programme.sport;

    return (
      (!query || getSearchableValues(entry).some((value) => normalise(value).includes(query)))
      && (filters.wing === "all" || entry.wing === filters.wing)
      && (!country || (place !== undefined && normalise(place.country) === country))
      && (!region || (place !== undefined && normalise(place.region) === region))
      && (!sportFamily || (sport !== undefined && normalise(sport.family) === sportFamily))
      && (!environment || (
        sport !== undefined
        && sport.environments.some((value) => normalise(value) === environment)
      ))
    );
  });
}

function uniqueLocaleSorted(values: string[]): string[] {
  return [...new Set(values)].sort((left, right) => left.localeCompare(right, "en-GB"));
}

export function getProgrammeFacets(entries: ProgrammeEntry[]): ProgrammeFacets {
  return {
    countries: uniqueLocaleSorted(entries.flatMap((entry) => (
      entry.programme.place ? [entry.programme.place.country] : []
    ))),
    regions: uniqueLocaleSorted(entries.flatMap((entry) => (
      entry.programme.place ? [entry.programme.place.region] : []
    ))),
    sportFamilies: uniqueLocaleSorted(entries.flatMap((entry) => (
      entry.programme.sport ? [entry.programme.sport.family] : []
    ))),
    environments: uniqueLocaleSorted(entries.flatMap((entry) => (
      entry.programme.sport ? entry.programme.sport.environments : []
    ))),
  };
}

export function getVisibleProgramme(entries: ProgrammeEntry[], visibleCount: number): {
  visible: ProgrammeEntry[];
  remaining: number;
} {
  const visible = entries.slice(0, Math.max(PROGRAMME_BATCH_SIZE, visibleCount));

  return {
    visible,
    remaining: Math.max(0, entries.length - visible.length),
  };
}
